import { useMemo, useEffect, useCallback, useState } from 'react';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import { useProject } from 'hooks/useProject';
import { SurveyListLatestContent } from 'interface/project/projectInfo';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { Paper, Radio, styled } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => <FormControlLabel {...props} />)(
  ({ theme, checked }) => ({
    '.MuiFormControlLabel-label': checked && {
      color: theme.palette.mind.black.A0,
      fontWeight: 500,
    },
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    fontWeight: 400,
  }),
);

const CustomCheckbox = styled(Radio)(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.palette.mind.custom.white,
    '& .MuiSvgIcon-root': {
      backgroundColor: theme.palette.mind.custom.white,

      '&::after': {
        borderColor: theme.palette.mind.custom.checkBorder,
        content: '""',
        display: 'block',
      },
    },
  },
}));

function CustomFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

function SurveyListRadioGroup(): JSX.Element {
  const { inputs, onChange } = useProjectInfoProvider();
  const [allLoading, setAllLoading] = useState(true);

  const { getInfiniteSurveyListLatest, getProjectIdDetail } = useProject();
  const { data: surveyData, size, setSize, isReachingEnd, isLoadingMore } = getInfiniteSurveyListLatest();
  const { data, isLoading } = getProjectIdDetail(inputs.projectId || '');

  const surveys: SurveyListLatestContent[] = useMemo(() => {
    return surveyData?.map(surveyListInfo => surveyListInfo.contents).reduce((pre, cur) => pre.concat(cur)) || [];
  }, [surveyData]);

  const surveyInfo = useMemo(() => {
    return data?.survey || {};
  }, [data?.survey]);

  useEffect(() => {
    if (!isLoading && surveyInfo?.id) {
      onChange('stepper2', {
        id: surveyInfo.id,
        serverId: surveyInfo.id,
      });
    }
  }, [isLoading, onChange, surveyInfo.id]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value;
    onChange('stepper2', {
      id: newValue,
    });
  };

  const loadMore = useCallback(() => {
    setSize(size + 1);
  }, [setSize, size]);

  useEffect(() => {
    if (allLoading && !isLoadingMore) {
      if (!isReachingEnd) {
        loadMore();
      } else {
        setAllLoading(false);
      }
    }
  }, [allLoading, isLoadingMore, isReachingEnd, loadMore]);

  return (
    <RadioGroup
      name="survey-radio-group"
      value={inputs.stepper2.id || ''}
      onChange={handleChange}
      sx={{ maxHeight: '500px', overflow: 'auto', flexWrap: 'nowrap' }}
    >
      {surveys.map(obj => (
        <Paper key={obj.id} elevation={3} sx={{ p: 1, m: 1, display: 'flex' }}>
          <CustomFormControlLabel
            value={obj.id}
            label={obj.surveyName}
            // disabled={typeof surveyInfo.id !== 'undefined' && surveyInfo.id !== ''}
            control={
              <CustomCheckbox
                checkedIcon={<CheckBoxIcon style={{ display: 'block', color: '#000000' }} fontSize="small" />}
                icon={<CheckBoxOutlineBlankIcon />}
              />
            }
          />
        </Paper>
      ))}
    </RadioGroup>
  );
}

export default SurveyListRadioGroup;
